import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { DropdownModule } from 'primeng/dropdown';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { Subscription } from 'rxjs';
import { FormCreatorService, IDependencyMap, TypeChecker } from 'lib-base';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-dependency-builder',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    ButtonModule,
    TableModule,
    DropdownModule,
    ToastModule, TranslateModule
  ],
  providers: [MessageService],
  template: `
    <p-toast></p-toast>
    <div class="grid formgrid p-fluid">
      <div class="field col-12">
        <button pButton type="button" icon="pi pi-plus" [label]="'dynamic-form.configuration.agregar-dependencia' | translate" 
          class="button-edit p-button-outlined" (click)="addDependency()"></button>
      </div>
      <div class="field col-12">
        <p-table [value]="dependencies" dataKey="id" editMode="row" styleClass="p-datatable-sm" [scrollable]="true" scrollHeight="250px">
          <ng-template pTemplate="header">
              <tr>
                  <th style="width:40%">Control</th>
                  <th style="width:40%">Field</th>
                  <th style="width:90px"></th>
              </tr>
          </ng-template>
          <ng-template pTemplate="body" let-dependencies let-editing="editing" let-ri="rowIndex">
              <tr [pEditableRow]="dependencies">
                  <td>
                      <p-cellEditor>
                          <ng-template pTemplate="input">
                              <p-dropdown [options]="validControls" [(ngModel)]="dependencies.control" appendTo="body" required></p-dropdown>
                          </ng-template>
                          <ng-template pTemplate="output">
                              {{dependencies.control}}
                          </ng-template>
                      </p-cellEditor>
                  </td>
                  <td>
                      <p-cellEditor>
                      <ng-template pTemplate="input">
                              <p-dropdown [options]="validMappedFields" [(ngModel)]="dependencies.mappedField" appendTo="body" required></p-dropdown>
                          </ng-template>
                          <ng-template pTemplate="output">
                              {{dependencies.mappedField}}
                          </ng-template>
                      </p-cellEditor>
                  </td>
                  <td>
                    <div class="flex align-items-center justify-content-center gap-2">
                      <button *ngIf="!editing" pButton type="button" pInitEditableRow icon="pi pi-pencil"
                          (click)="onRowEditInit(dependencies)" class="p-button-rounded p-button-text"></button>
                      <button *ngIf="!editing" pButton type="button" icon="pi pi-times"
                          (click)="onDeleteRow(ri)" class="p-button-rounded p-button-text p-button-danger"></button>
                      <button *ngIf="editing" pButton type="button" pSaveEditableRow icon="pi pi-check"
                          (click)="onRowEditSave(dependencies)" class="p-button-rounded p-button-text p-button-success mr-2"></button>
                      <button *ngIf="editing" pButton type="button" pCancelEditableRow icon="pi pi-times"
                          (click)="onRowEditCancel(dependencies, ri)" class="p-button-rounded p-button-text p-button-danger"></button>
                    </div>
                  </td>
              </tr>
          </ng-template>
        </p-table>
      </div>
    </div>
  `,
  styleUrls: ['./dependency-builder.component.scss']
})
export class DependencyBuilderComponent implements OnInit, OnDestroy {
  private _controlNames: string[]
  private _subscriptionShowConfiguration !: Subscription;
  dependencies: any[] = [];
  clonedDependencies: { [s: number]: any; } = {};
  validControls: string[] = [];
  validMappedFields: string[] = [];
  idCounter: number = 0;

  constructor(private messageService: MessageService, private fc: FormCreatorService) {
    this._controlNames = this.fc.Form.getControlNamesWithOptions();
    this.bindDependencies();
  }

  ngOnInit(): void {
    this._subscriptionShowConfiguration = this.fc.showConfigurationEvent.subscribe(() => {
      this.bindDependencies();
    });
  }

  ngOnDestroy() {
    this._subscriptionShowConfiguration.unsubscribe();
  }

  get Type() {
    return this.fc.ConfigurationSection.type;
  }

  get Page() {
    return this.fc.ConfigurationSection.page;
  }

  get Control() {
    return this.fc.ConfigurationSection.control;
  }
  
  bindDependencies() {
    if (this.Control && TypeChecker.hasOptions(this.Control)) {
      this._controlNames = this.fc.Form.getControlNamesWithOptions();
      // Bind the dependency map whenever needed, not just when the component is instantiated
      this.idCounter = 0;
      this.dependencies = [...this.Control.dependencyMap].map((dep: IDependencyMap) => {
        return { id: this.idCounter++, control: dep.control, mappedField: dep.mappedField };
      });
    }
  }

  SetValidOptions(controlName: string = "") {
    if (this.Control && TypeChecker.hasOptions(this.Control)) {
      this.validControls = this._controlNames.filter((control) => {
        if (this.Control)
          return control !== this.Control.name && !this.dependencies.some(dep => dep.control === control && dep.control !== controlName);
        return false;
      });

      this.validMappedFields = this.Control.getOptionKeys().filter((field) => {
        return field !== "text" && field !== "value";
      });
    }
  }

  addDependency() {
    this.SetValidOptions();
    this.idCounter++;
    const newDependency = { id: this.idCounter, control: '', mappedField: '' };
    this.dependencies = [...this.dependencies, newDependency];
  }

  onRowEditInit(dependency: any) {
    this.SetValidOptions(dependency.control);
    this.clonedDependencies[dependency.id] = { ...dependency };
  }

  onDeleteRow(index: number) {
    let updateddep = [...this.dependencies];
    updateddep.splice(index, 1);
    this.dependencies = updateddep;
    this.SetValidOptions();
    if (this.Control && TypeChecker.hasOptions(this.Control)) {
      this.fc.Form.updateChoiceOptionProperties(this.Control, this.dependencies);
    }
  }

  onRowEditSave(dependency: any) {
    if (dependency.control === "" || dependency.mappedField === "") {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'All fields must have values' });
      return;
    }

    delete this.clonedDependencies[dependency.id];
    this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Dependency updated' });
    this.SetValidOptions();
    if (this.Control && TypeChecker.hasOptions(this.Control)) {
      this.fc.Form.updateChoiceOptionProperties(this.Control, this.dependencies);
    }
  }

  onRowEditCancel(dependency: any, index: number) {
    this.dependencies[index] = this.clonedDependencies[dependency.id];
    delete this.clonedDependencies[dependency.id];
  }
}
